import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function Languages() {
  const langs = [
    { code: 'ckb', name: 'کوردی (سۆرانی)', native: 'کوردی', flag: '🇮🇶' },
    { code: 'ar', name: 'العربية', native: 'العربية', flag: '🇸🇦' },
    { code: 'en', name: 'English', native: 'English', flag: '🇺🇸' },
  ];

  const dropdownRef = useRef(null);
  const { i18n } = useTranslation();

  const initialLang =
    i18n && i18n.language
      ? i18n.language
      : localStorage.getItem('lang') || 'ckb';
  const [currentLang, setCurrentLang] = useState(initialLang);
  const [isOpen, setIsOpen] = useState(false);
  const [mobileSheet, setMobileSheet] = useState(false);

  const isRtl = ['ar', 'ckb'].includes(currentLang);
  const currentLangInfo = langs.find((l) => l.code === currentLang) || langs[2];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (i18n && typeof i18n.changeLanguage === 'function') {
      i18n.changeLanguage(currentLang).catch(() => {});
    }
    document.documentElement.lang = currentLang;
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    localStorage.setItem('lang', currentLang);
  }, [currentLang, i18n, isRtl]);

  // Lock body when mobile sheet is open
  useEffect(() => {
    if (mobileSheet) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileSheet]);

  const handleSelect = (code) => {
    setCurrentLang(code);
    setIsOpen(false);
    setMobileSheet(false);
  };

  const openDesktop = () => setIsOpen((v) => !v);
  const openMobile = () => setMobileSheet(true);

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* ── Desktop Trigger ── */}
        <button
          onClick={openDesktop}
          className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] font-semibold transition-all duration-300 ${
            isOpen
              ? 'bg-white/[0.12] text-white ring-1 ring-violet-500/40'
              : 'text-white/60 hover:text-white hover:bg-white/[0.06]'
          }`}
          aria-label="Select language"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <span className="text-base leading-none">{currentLangInfo.flag}</span>
          <span>{currentLangInfo.native}</span>
          <svg
            className={`w-3 h-3 text-white/40 transition-transform duration-300 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* ── Mobile Trigger ── */}
        <button
          onClick={openMobile}
          className="md:hidden flex items-center justify-center w-9 h-9 rounded-xl text-white/60 hover:text-white hover:bg-white/[0.06] transition-all duration-300"
          aria-label="Select language"
        >
          <span className="text-lg leading-none">{currentLangInfo.flag}</span>
        </button>

        {/* ── Desktop Dropdown ── */}
        <div
          className={`hidden md:block absolute ${
            isRtl ? 'left-0' : 'right-0'
          } mt-2 w-60 z-50 transition-all duration-300 origin-top ${
            isOpen
              ? 'opacity-100 scale-100 translate-y-0'
              : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
          }`}
          role="menu"
        >
          <div className="bg-[#1a1a2e]/95 backdrop-blur-2xl rounded-2xl shadow-2xl shadow-black/40 border border-white/[0.08] overflow-hidden">
            {/* Header */}
            <div className="px-4 pt-4 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-violet-500/15 flex items-center justify-center">
                  <svg
                    className="w-3.5 h-3.5 text-violet-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                    />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-white/40 uppercase tracking-widest">
                  {isRtl ? 'زمان' : 'Language'}
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="mx-3">
              <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
            </div>

            {/* Options */}
            <div className="p-2 space-y-0.5">
              {langs.map((lang) => {
                const active = currentLang === lang.code;
                return (
                  <button
                    key={lang.code}
                    onClick={() => handleSelect(lang.code)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 group ${
                      active
                        ? 'bg-violet-500/15 text-white'
                        : 'text-white/50 hover:text-white hover:bg-white/[0.04]'
                    }`}
                    role="menuitem"
                  >
                    {/* Flag */}
                    <span className="text-lg leading-none w-7 text-center">
                      {lang.flag}
                    </span>

                    {/* Name */}
                    <div className="flex flex-col items-start flex-1 min-w-0">
                      <span className="font-semibold text-[13px] truncate">
                        {lang.name}
                      </span>
                      {lang.name !== lang.native && (
                        <span
                          className={`text-[11px] mt-0.5 ${
                            active ? 'text-violet-300/70' : 'text-white/25'
                          }`}
                        >
                          {lang.native}
                        </span>
                      )}
                    </div>

                    {/* Active indicator */}
                    <div
                      className={`flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-300 flex-shrink-0 ${
                        active
                          ? 'border-violet-400 bg-violet-500'
                          : 'border-white/15 group-hover:border-white/25'
                      }`}
                    >
                      {active && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer subtle gradient */}
            <div className="h-1 bg-gradient-to-r from-violet-600/20 via-indigo-600/20 to-violet-600/20" />
          </div>
        </div>
      </div>

      {/* ── Mobile Bottom Sheet ── */}
      <div
        className={`md:hidden fixed inset-0 z-[100] transition-all duration-500 ${
          mobileSheet ? 'visible' : 'invisible'
        }`}
        aria-hidden={!mobileSheet}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${
            mobileSheet ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileSheet(false)}
        />

        {/* Sheet */}
        <div
          className={`absolute inset-x-0 bottom-0 transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${
            mobileSheet ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div className="bg-[#1a1a2e]/98 backdrop-blur-2xl rounded-t-3xl shadow-2xl shadow-black/50 border-t border-white/[0.08] max-h-[85vh] overflow-hidden mx-auto max-w-lg">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-white/15" />
            </div>

            {/* Header */}
            <div className="px-6 pt-4 pb-5 text-center">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-violet-500/25">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                  />
                </svg>
              </div>
              <h3 className="text-white font-bold text-lg">
                {isRtl ? 'زمان هەڵبژێرە' : 'Select Language'}
              </h3>
              <p className="text-white/30 text-sm mt-1">
                {isRtl
                  ? 'زمانی دڵخوازت هەڵبژێرە'
                  : 'Choose your preferred language'}
              </p>
            </div>

            {/* Divider */}
            <div className="mx-6">
              <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
            </div>

            {/* Language Options */}
            <div className="p-4 space-y-2">
              {langs.map((lang, index) => {
                const active = currentLang === lang.code;
                return (
                  <button
                    key={lang.code}
                    onClick={() => handleSelect(lang.code)}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 active:scale-[0.98] ${
                      active
                        ? 'bg-gradient-to-r from-violet-600/20 to-indigo-600/15 border border-violet-500/25 shadow-lg shadow-violet-500/10'
                        : 'bg-white/[0.03] border border-white/[0.04] hover:bg-white/[0.06] hover:border-white/[0.08]'
                    }`}
                    style={{
                      transitionDelay: mobileSheet
                        ? `${(index + 1) * 75}ms`
                        : '0ms',
                      opacity: mobileSheet ? 1 : 0,
                      transform: mobileSheet
                        ? 'translateY(0)'
                        : 'translateY(12px)',
                      transition:
                        'all 0.5s cubic-bezier(0.32, 0.72, 0, 1)',
                    }}
                  >
                    {/* Flag */}
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${
                        active
                          ? 'bg-violet-500/20 shadow-inner'
                          : 'bg-white/[0.04]'
                      }`}
                    >
                      {lang.flag}
                    </div>

                    {/* Text */}
                    <div className="flex flex-col items-start flex-1 min-w-0">
                      <span
                        className={`font-semibold text-[15px] ${
                          active ? 'text-white' : 'text-white/70'
                        }`}
                      >
                        {lang.name}
                      </span>
                      {lang.name !== lang.native && (
                        <span
                          className={`text-[13px] mt-0.5 ${
                            active ? 'text-violet-300/70' : 'text-white/25'
                          }`}
                        >
                          {lang.native}
                        </span>
                      )}
                    </div>

                    {/* Radio indicator */}
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        active
                          ? 'border-violet-400 bg-violet-500 shadow-lg shadow-violet-500/30'
                          : 'border-white/15'
                      }`}
                    >
                      {active && (
                        <svg
                          className="w-3.5 h-3.5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Close button */}
            <div className="p-4 pt-2">
              <button
                onClick={() => setMobileSheet(false)}
                className="w-full py-3.5 rounded-2xl bg-white/[0.06] border border-white/[0.06] text-white/50 font-semibold text-[15px] hover:bg-white/[0.1] hover:text-white/70 transition-all duration-300 active:scale-[0.98]"
              >
                {isRtl ? 'داخستن' : 'Close'}
              </button>
            </div>

            {/* Safe area spacer for iOS */}
            <div className="h-2" />
          </div>
        </div>
      </div>
    </>
  );
}

export default Languages;